import React, { useEffect, useRef, useState } from "react";
import { BarcodeDetector, prepareZXingModule } from "barcode-detector/ponyfill";
import { api, formatApiErrorDetail } from "../lib/api";

// Serve the zxing WASM binary from our own server (no CDN dependency)
prepareZXingModule({
  overrides: {
    locateFile: (path, prefix) =>
      path.endsWith(".wasm") ? `/wasm/${path}` : prefix + path,
  },
});
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Plus,
  Minus,
  Camera,
  CameraOff,
  Keyboard,
  CheckCircle2,
  RotateCcw,
  ArrowRightLeft,
  Zap,
  ZapOff,
} from "lucide-react";
import { toast } from "sonner";
import { Link } from "react-router-dom";

const MODES = {
  zugang: { key: "zugang", label: "Zugang", color: "#16A34A", action: "HINZUFÜGEN", sign: 1 },
  entnahme: { key: "entnahme", label: "Entnahme", color: "#DC2626", action: "ENTNEHMEN", sign: -1 },
  rueckbuchung: { key: "rueckbuchung", label: "Rückbuchung", color: "#2563EB", action: "ZURÜCKBUCHEN", sign: 1 },
  umlagerung: { key: "umlagerung", label: "Umlagern", color: "#7C3AED", action: "UMLAGERN", sign: 0 },
};

const LOC_OPTIONS = [
  { value: "main", label: "Hauptlager", color: "#F97316" },
  { value: "buffer", label: "Zwischenlager", color: "#0EA5E9" },
  { value: "pickup", label: "Abhollager", color: "#9333EA" },
];

const LOC_FIELD = { main: "quantity", buffer: "quantity_buffer", pickup: "quantity_pickup" };

export default function Scanner() {
  const usbInputRef = useRef(null);
  const videoRef = useRef(null);
  const articlePanelRef = useRef(null);
  const streamRef = useRef(null);
  const detectorRef = useRef(null);
  const scanningRef = useRef(false);
  const loopTimerRef = useRef(null);
  const [camOn, setCamOn] = useState(false);
  const [torchOn, setTorchOn] = useState(false);
  const [torchSupported, setTorchSupported] = useState(false);
  const [article, setArticle] = useState(null);
  const [amount, setAmount] = useState(1);
  const [mode, setMode] = useState("entnahme");
  const [project, setProject] = useState("");
  const [location, setLocation] = useState("main"); // for adjust modes
  const [fromLoc, setFromLoc] = useState("main"); // for transfer
  const [toLoc, setToLoc] = useState("buffer");
  const [lastAction, setLastAction] = useState(null);

  useEffect(() => {
    usbInputRef.current?.focus();
    const handler = () => {
      if (document.activeElement === document.body) usbInputRef.current?.focus();
    };
    document.addEventListener("click", handler);
    return () => document.removeEventListener("click", handler);
  }, []);

  const lookup = async (val) => {
    const code = val.trim();
    if (!code) return;
    try {
      const { data } = await api.get(`/articles/by-ean/${code}`);
      setArticle(data);
      toast.success(`Gefunden: ${data.name}`);
      // Mobile: bring the article panel into view after a successful scan
      setTimeout(() => {
        articlePanelRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 150);
    } catch (e) {
      setArticle(null);
      toast.error("Kein Artikel mit dieser EAN gefunden.");
    }
  };

  const onUsbKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      lookup(e.currentTarget.value);
      e.currentTarget.value = "";
    }
  };

  const stopCam = () => {
    scanningRef.current = false;
    clearTimeout(loopTimerRef.current);
    const stream = streamRef.current;
    streamRef.current = null;
    if (stream) stream.getTracks().forEach((t) => t.stop());
    if (videoRef.current) videoRef.current.srcObject = null;
    setTorchOn(false);
    setTorchSupported(false);
    setCamOn(false);
  };

  const detectLoop = async () => {
    if (!scanningRef.current) return;
    const video = videoRef.current;
    if (video && video.readyState >= 2 && detectorRef.current) {
      try {
        const codes = await detectorRef.current.detect(video);
        if (codes.length > 0 && scanningRef.current) {
          scanningRef.current = false;
          const value = codes[0].rawValue;
          if (navigator.vibrate) navigator.vibrate(80);
          stopCam();
          lookup(value);
          return;
        }
      } catch (_) {
        /* frame not ready — try again */
      }
    }
    loopTimerRef.current = setTimeout(detectLoop, 120);
  };

  const startCam = async () => {
    if (streamRef.current) return; // already running
    try {
      if (!detectorRef.current) {
        detectorRef.current = new BarcodeDetector({
          formats: ["ean_13", "ean_8", "upc_a", "upc_e", "code_128", "qr_code"],
        });
      }
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: false,
        video: {
          facingMode: { ideal: "environment" },
          width: { ideal: 1920 },
          height: { ideal: 1080 },
        },
      });
      streamRef.current = stream;
      setCamOn(true);
      const video = videoRef.current;
      video.srcObject = stream;
      await video.play();
      // Continuous autofocus + torch capability (where supported)
      const track = stream.getVideoTracks()[0];
      try {
        const caps = track.getCapabilities ? track.getCapabilities() : {};
        if (Array.isArray(caps.focusMode) && caps.focusMode.includes("continuous")) {
          await track.applyConstraints({ advanced: [{ focusMode: "continuous" }] });
        }
        setTorchSupported(!!caps.torch);
      } catch (_) {
        /* capabilities not supported */
      }
      scanningRef.current = true;
      detectLoop();
    } catch (e) {
      stopCam();
      toast.error("Kamera konnte nicht gestartet werden. Bitte Kamerazugriff erlauben.");
    }
  };

  const toggleTorch = async () => {
    const track = streamRef.current?.getVideoTracks()[0];
    if (!track) return;
    try {
      await track.applyConstraints({ advanced: [{ torch: !torchOn }] });
      setTorchOn(!torchOn);
    } catch (_) {
      toast.error("Taschenlampe wird von diesem Gerät nicht unterstützt.");
    }
  };

  // Cleanup on unmount — stop camera stream and detection loop
  useEffect(() => {
    return () => {
      scanningRef.current = false;
      clearTimeout(loopTimerRef.current);
      const stream = streamRef.current;
      streamRef.current = null;
      if (stream) stream.getTracks().forEach((t) => t.stop());
    };
  }, []);

  const submit = async () => {
    if (!article) return;
    const m = MODES[mode];
    const qty = Math.max(1, Number(amount) || 1);

    if (mode === "umlagerung") {
      if (fromLoc === toLoc) {
        toast.error("Quelle und Ziel sind identisch.");
        return;
      }
      try {
        const { data } = await api.post(`/articles/${article.id}/transfer`, {
          from_loc: fromLoc,
          to_loc: toLoc,
          qty,
          project: project.trim(),
        });
        setArticle(data);
        setLastAction({
          delta: qty,
          name: data.name,
          qty: data[LOC_FIELD[toLoc]],
          project: project.trim(),
          mode,
          info: `${labelOf(fromLoc)} → ${labelOf(toLoc)}`,
        });
        toast.success(`Umlagerung gebucht — ${qty} Stk. ${labelOf(fromLoc)} → ${labelOf(toLoc)}`);
      } catch (e) {
        toast.error(formatApiErrorDetail(e.response?.data?.detail));
      }
      return;
    }

    const delta = m.sign * qty;
    if (mode === "entnahme" && !project.trim()) {
      toast.error("Bitte ein Projekt angeben.");
      return;
    }
    try {
      const { data } = await api.post(`/articles/${article.id}/adjust`, {
        delta,
        project: project.trim(),
        type: m.key,
        location,
      });
      setArticle(data);
      setLastAction({
        delta,
        name: data.name,
        qty: data[LOC_FIELD[location]],
        project: project.trim(),
        mode,
        info: labelOf(location),
      });
      toast.success(`${m.label} gebucht — ${labelOf(location)}: ${data[LOC_FIELD[location]]}`);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const reset = () => {
    setArticle(null);
    setAmount(1);
    setProject("");
    setLastAction(null);
    usbInputRef.current?.focus();
  };

  const m = MODES[mode];
  const isTransfer = mode === "umlagerung";

  return (
    <div className="max-w-[1200px] mx-auto space-y-5" data-testid="scanner-page">
      <div>
        <div className="mono text-[11px] tracking-[0.25em] text-zinc-500 mb-2">
          BESTAND BUCHEN · 4 MODI · 3 LAGER
        </div>
        <h1 className="font-display text-3xl sm:text-4xl uppercase">Scanner</h1>
      </div>

      {/* Mode switcher — 4 buttons */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2" data-testid="scanner-mode-switcher">
        {Object.values(MODES).map((opt) => {
          const active = mode === opt.key;
          const labelTop =
            opt.key === "zugang" ? "+ ZUGANG" :
            opt.key === "entnahme" ? "− ENTNAHME" :
            opt.key === "rueckbuchung" ? "↺ RÜCKBUCHUNG" :
            "⇄ UMLAGERN";
          return (
            <button
              key={opt.key}
              onClick={() => setMode(opt.key)}
              data-testid={`mode-${opt.key}`}
              className={`p-3 sm:p-4 rounded-sm border-2 text-left transition-colors ${
                active ? "border-black bg-black text-white" : "border-zinc-300 bg-white hover:bg-zinc-50"
              }`}
            >
              <div
                className="mono text-[10px] tracking-[0.2em] font-bold"
                style={{ color: opt.color }}
              >
                {labelTop}
              </div>
              <div className={`font-display text-base sm:text-lg uppercase mt-1 ${active ? "text-white" : ""}`}>
                {opt.label}
              </div>
            </button>
          );
        })}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
        {/* Scanner area */}
        <div className="lg:col-span-3 bg-white border border-zinc-200 rounded-sm p-4 sm:p-6">
          <div className="flex items-center justify-between mb-4">
            <div className="mono text-[10px] tracking-[0.25em] text-zinc-500">EAN ERFASSEN</div>
            {!camOn ? (
              <Button
                onClick={startCam}
                data-testid="start-camera-button"
                className="bg-black hover:bg-zinc-800 text-white rounded-sm h-9"
              >
                <Camera className="w-4 h-4 mr-2" /> KAMERA
              </Button>
            ) : (
              <Button
                onClick={stopCam}
                data-testid="stop-camera-button"
                className="bg-red-600 hover:bg-red-700 text-white rounded-sm h-9"
              >
                <CameraOff className="w-4 h-4 mr-2" /> STOPP
              </Button>
            )}
          </div>

          <div
            className={`relative w-full aspect-video bg-black border-2 border-dashed border-zinc-700 rounded-sm overflow-hidden ${
              camOn ? "" : "opacity-90"
            }`}
            data-testid="scanner-region-wrapper"
          >
            <video
              ref={videoRef}
              playsInline
              muted
              autoPlay
              className={`absolute inset-0 w-full h-full object-cover ${camOn ? "" : "hidden"}`}
              data-testid="scanner-video"
            />
            {!camOn && (
              <div className="absolute inset-0 flex items-center justify-center">
                <div className="text-center px-4">
                  <Camera className="w-10 h-10 text-zinc-600 mx-auto mb-2" strokeWidth={1.5} />
                  <div className="mono text-[11px] tracking-[0.25em] text-zinc-500">
                    KAMERA AUS · ODER USB-SCANNER NUTZEN
                  </div>
                </div>
              </div>
            )}
            {camOn && (
              <div className="absolute inset-0 pointer-events-none flex items-center justify-center">
                <div className="w-64 h-32 border-2 border-[#F97316] animate-pulse-orange rounded-sm" />
              </div>
            )}
            {camOn && torchSupported && (
              <button
                onClick={toggleTorch}
                data-testid="torch-toggle-button"
                className={`absolute bottom-3 right-3 w-11 h-11 rounded-full flex items-center justify-center border-2 transition-colors ${
                  torchOn
                    ? "bg-[#F97316] border-[#F97316] text-white"
                    : "bg-black/60 border-white/50 text-white"
                }`}
                aria-label="Taschenlampe"
              >
                {torchOn ? <Zap className="w-5 h-5" /> : <ZapOff className="w-5 h-5" />}
              </button>
            )}
          </div>

          <div className="mt-5">
            <Label className="text-xs uppercase tracking-wider font-semibold flex items-center gap-2">
              <Keyboard className="w-4 h-4" /> USB-Scanner / manuelle Eingabe
            </Label>
            <Input
              ref={usbInputRef}
              onKeyDown={onUsbKeyDown}
              data-testid="usb-scanner-input"
              placeholder="EAN scannen oder eingeben + ENTER"
              className="mt-1.5 rounded-sm border-zinc-400 h-12 font-mono-num text-base focus-visible:ring-[#F97316] focus-visible:ring-2"
              autoFocus
            />
          </div>
        </div>

        {/* Action panel */}
        <div className="lg:col-span-2 space-y-4" ref={articlePanelRef} data-testid="scan-action-panel">
          {!article ? (
            <div className="bg-white border border-zinc-200 rounded-sm p-6 text-center">
              <div className="mono text-[10px] tracking-[0.25em] text-zinc-500 mb-3">
                KEIN ARTIKEL GEWÄHLT
              </div>
              <div className="text-sm text-zinc-600">
                Scanne einen EAN oder gib ihn manuell ein, um den Artikel zu laden.
              </div>
            </div>
          ) : (
            <>
              <div
                className="bg-white border border-zinc-200 rounded-sm p-5"
                style={{ borderLeft: `4px solid ${m.color}` }}
              >
                <div className="mono text-[10px] tracking-[0.25em] text-zinc-500">ARTIKEL</div>
                <div className="font-display text-xl mt-1 leading-tight" data-testid="scan-article-name">
                  {article.name}
                </div>
                <div className="mono text-xs text-zinc-500 mt-1">{article.ean}</div>
                <div className="grid grid-cols-3 gap-2 mt-3">
                  {LOC_OPTIONS.map((l) => (
                    <div key={l.value} className="text-center bg-zinc-50 rounded-sm py-2 border border-zinc-100">
                      <div className="mono text-[9px] tracking-wider" style={{ color: l.color }}>
                        {l.label.toUpperCase()}
                      </div>
                      <div className="font-display text-xl font-mono-num">
                        {article[LOC_FIELD[l.value]] || 0}
                      </div>
                    </div>
                  ))}
                </div>
                {article.is_set && (article.set_components || []).length > 0 && (
                  <div className="mt-3 border border-zinc-900 rounded-sm" data-testid="scan-set-contents">
                    <div className="bg-zinc-900 text-white mono text-[10px] tracking-[0.25em] px-3 py-1.5">
                      SET-INHALT ({(article.set_components || []).reduce((s, c) => s + (Number(c.qty) || 0), 0)} TEILE)
                    </div>
                    <ul className="px-3 py-2 space-y-1">
                      {(article.set_components || []).map((c, i) => (
                        <li key={i} className="text-sm flex items-center gap-2">
                          <span className="font-mono-num font-bold w-10 text-right shrink-0">{c.qty}×</span>
                          <span className="truncate">{c.name || "—"}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>

              <div className="bg-white border border-zinc-200 rounded-sm p-5 space-y-4">
                {isTransfer ? (
                  <>
                    <div>
                      <Label className="text-xs uppercase tracking-wider font-semibold">Von</Label>
                      <Select value={fromLoc} onValueChange={setFromLoc}>
                        <SelectTrigger className="mt-1.5 rounded-sm border-zinc-400 h-11" data-testid="transfer-from-select">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent className="rounded-sm">
                          {LOC_OPTIONS.map((l) => (
                            <SelectItem key={l.value} value={l.value}>{l.label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="text-center text-zinc-400">
                      <ArrowRightLeft className="w-5 h-5 mx-auto" />
                    </div>
                    <div>
                      <Label className="text-xs uppercase tracking-wider font-semibold">Nach</Label>
                      <Select value={toLoc} onValueChange={setToLoc}>
                        <SelectTrigger className="mt-1.5 rounded-sm border-zinc-400 h-11" data-testid="transfer-to-select">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent className="rounded-sm">
                          {LOC_OPTIONS.map((l) => (
                            <SelectItem key={l.value} value={l.value}>{l.label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </>
                ) : (
                  <div>
                    <Label className="text-xs uppercase tracking-wider font-semibold">Lager</Label>
                    <Select value={location} onValueChange={setLocation}>
                      <SelectTrigger className="mt-1.5 rounded-sm border-zinc-400 h-11" data-testid="scan-location-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="rounded-sm">
                        {LOC_OPTIONS.map((l) => (
                          <SelectItem key={l.value} value={l.value}>{l.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}

                <div>
                  <Label className="text-xs uppercase tracking-wider font-semibold">Menge</Label>
                  <Input
                    type="number"
                    min={1}
                    value={amount}
                    onChange={(e) => setAmount(Number(e.target.value))}
                    data-testid="scan-amount-input"
                    className="mt-1.5 rounded-sm border-zinc-400 font-mono-num text-2xl h-14 text-center"
                  />
                </div>

                <div>
                  <Label className="text-xs uppercase tracking-wider font-semibold">
                    Projekt {mode === "entnahme" && <span className="text-red-600">*</span>}
                  </Label>
                  <Input
                    value={project}
                    onChange={(e) => setProject(e.target.value)}
                    placeholder={
                      mode === "entnahme" ? "Pflicht — z.B. Bauvorhaben Müller" :
                      mode === "umlagerung" ? "z.B. Bauvorhaben Schmidt (für Zwischenlager)" :
                      "Optional"
                    }
                    data-testid="scan-project-input"
                    className="mt-1.5 rounded-sm border-zinc-400 h-12"
                  />
                </div>

                <Button
                  onClick={submit}
                  data-testid="scan-submit-button"
                  className="w-full h-16 text-white rounded-sm text-lg font-bold tracking-wide"
                  style={{ backgroundColor: m.color }}
                >
                  {mode === "entnahme" ? <Minus className="w-5 h-5 mr-2" /> :
                   mode === "rueckbuchung" ? <RotateCcw className="w-5 h-5 mr-2" /> :
                   mode === "umlagerung" ? <ArrowRightLeft className="w-5 h-5 mr-2" /> :
                   <Plus className="w-5 h-5 mr-2" />}
                  {m.action}
                </Button>

                <div className="grid grid-cols-2 gap-2">
                  <Link
                    to={`/artikel/${article.id}`}
                    className="bg-white border border-zinc-400 hover:bg-zinc-50 px-4 h-10 rounded-sm text-sm font-medium flex items-center justify-center"
                  >
                    Bearbeiten
                  </Link>
                  <Button
                    onClick={reset}
                    variant="outline"
                    data-testid="scan-reset-button"
                    className="rounded-sm border-zinc-400 h-10"
                  >
                    Nächster Scan
                  </Button>
                </div>
              </div>

              {lastAction && (
                <div
                  className="border rounded-sm p-4 flex items-center gap-3"
                  style={{
                    background: "#F8FAFC",
                    borderColor: "#E2E8F0",
                  }}
                >
                  <CheckCircle2 className="w-5 h-5" style={{ color: MODES[lastAction.mode].color }} />
                  <div className="text-sm">
                    <span className="font-semibold">
                      {lastAction.mode === "umlagerung" ? `${lastAction.delta} Stk.` :
                       lastAction.delta > 0 ? `+${lastAction.delta}` : lastAction.delta}
                    </span>{" "}
                    <span className="font-medium">{lastAction.name}</span>
                    {lastAction.info && <> · <em>{lastAction.info}</em></>}
                    {lastAction.project && <> · <em>{lastAction.project}</em></>}
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function labelOf(loc) {
  return LOC_OPTIONS.find((l) => l.value === loc)?.label || loc;
}
